/* eslint-disable react/no-unsafe */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";
import _ from "underscore";

import Button from "@khanacademy/wonder-blocks-button";
import {actionStyles} from "@khanacademy/wonder-blocks-styles";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {saveAndUpdateProfile} from "#data-access/user-profile-provider.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import $ from "#jquery/jquery.ts";

import AvatarDrawer from "./avatar-drawer.tsx";
import AvatarPartsAccordion from "./avatar-parts-accordion.tsx";
import AvatarPreview from "./avatar-preview.tsx";
import {fetchAvatars} from "./fetch-avatars.ts";
import FlippableCardView from "./flippable-card-view.tsx";
import type {
    AvatarModel,
    AvatarPart,
    AvatarPartType,
    AvatarsAnimation,
} from "./types.ts";
import ViewSwitcherButton from "./view-switcher-button.tsx";

// Unfortunately, browsers won't always consistently fire the
// transitionend/animationend event, especially when there are lots of
// elements moving in and out, such as when the user quickly switches between
// avatars. So instead of relying on the event being fired, we hardcode the
// transition duration here and use AphroditeCSSTransitionGroup.
// Keep in sync with avatar-customizer.less.
export const AVATAR_TRANSITION_DURATION_MS = 150;

const styles = StyleSheet.create({
    background: {
        backgroundColor: "#F7F5F4",
        backgroundPosition: "center",
        backgroundSize: "cover",
        float: "left",
        height: "100%",
        position: "relative",
        width: "100%",
    },
});

/**
 * Simple cache of all avatars loaded from the server. This will be refreshed
 * each time the avatar customizer is loaded, but we will load any
 * previously-cached data to minimize time to render customizer data.
 */
let avatarModelsCache: Array<AvatarModel> = [];

/**
 * Simple cache of all avatar parts loaded from the server.
 * Populated in tandem with the avatarModels cache above.
 */
let avatarPartsCache: Array<AvatarPart> = [];

type Props = {
    hasChangedAvatar: boolean;
    onClose: () => void;
    initialSelectionName: string;
} & WithApolloProps &
    WithLocationProps &
    WithActionSchedulerProps;

type State = {
    selectedType: AvatarPartType;
    avatarModels: Array<AvatarModel>;
    avatarParts: Array<AvatarPart>;
    showingModelGrid: boolean;
    selectedModel: string;
    selectedPart: string;
};

/**
 * Component that renders the contents of the "Customize your avatar" modal in
 * user profiles
 */
class AvatarCustomizer extends React.Component<Props, State> {
    state: State = {
        selectedType: "style",
        avatarModels: avatarModelsCache,
        avatarParts: avatarPartsCache,
        showingModelGrid: !(
            this.props.hasChangedAvatar || this.props.initialSelectionName
        ),
        selectedModel: "blobby",
        selectedPart: "blobby_green_style",
    };

    // TODO(jangmi, LP-7300): Remove usage of `UNSAFE_componentWillMount`
    UNSAFE_componentWillMount() {
        this._transformData(this.state);
    }

    componentDidMount() {
        this._isMounted = true;

        this._fetchData();
        $("#page-container").addClass("modal-background-blur");

        // @ts-expect-error - TS2769 - No overload matches this call.
        $(window).on("keyup", this.handleWindowKeyUp);
    }

    UNSAFE_componentWillUpdate(nextProps: Props, nextState: State) {
        this._transformData(nextState);
    }

    componentWillUnmount() {
        $("#page-container").removeClass("modal-background-blur");
        $("#top-header-container").removeClass("modal-background-blur");
        // @ts-expect-error - TS2769 - No overload matches this call.
        $(window).off("keyup", this.handleWindowKeyUp);

        this._isMounted = false;
    }

    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted = false;
    _indexOfAvatarModel: Record<string, number> = {};
    _allAvatarStyles: Array<AvatarPart> = [];
    _changedPartType: AvatarPartType | null | undefined = null;
    _modelIncrementedBy: number | null | undefined = null;
    _prevSelectedType: AvatarPartType | null | undefined = null;

    _setInitialSelection() {
        const partName =
            this.props.initialSelectionName ||
            this.props.location.queryParams["selected_avatar_part"];
        if (!partName) {
            return;
        }

        const selectedModel = _.findWhere(this.state.avatarModels, {
            name: partName,
        });
        if (selectedModel) {
            this._setSelectedModel(partName);
        } else {
            this._setSelectedPartFromName(partName);
        }

        // If we have an initial part selection, force show it in preview view.
        this.setState({showingModelGrid: false});
    }

    _fetchData() {
        fetchAvatars(this.props.gqlClient).then((result) => {
            if (result) {
                avatarModelsCache = result.avatarModels;
                avatarPartsCache = result.avatarParts;
                if (this._isMounted) {
                    this.setState({
                        avatarModels: avatarModelsCache,
                        selectedModel: result.selectedModel,
                        avatarParts: avatarPartsCache,
                        selectedPart: result.selectedPart,
                    });
                    this._setInitialSelection();
                }
            }
        });
    }

    _transformData(state: State) {
        const avatarParts = state.avatarParts;

        if (!avatarParts) {
            // If the data hasn't been set in state yet, let's return out safely.
            // This method will run again when the component updates after the state
            // has been successfully set.
            return;
        }

        this._allAvatarStyles = avatarParts;
        // @ts-expect-error - TS2322 - Type 'Dictionary<string | number>' is not assignable to type '{ [key: string]: number; }'.
        this._indexOfAvatarModel = _.object(
            state.avatarModels.map((model, index) => [model.name, index]),
        );
    }

    _getAvatarPart(partName: string): AvatarPart | null | undefined {
        if (!this._allAvatarStyles) {
            return null;
        }

        return _.findWhere(this._allAvatarStyles, {name: partName});
    }

    _setSelectedModel(modelName: string) {
        const oldModelName = this.state.selectedModel;

        // If the user changed model, then select the first style available.
        // TODO(david): Perhaps we should save which styles a user has selected
        //     with each model and revert to that when the user changes models.
        if (modelName !== oldModelName) {
            // TODO(benkomalo): will need to do this for all model specific
            // parts.
            const matchingStyles = _.filter(
                this._allAvatarStyles,
                // @ts-expect-error - TS2339 - Property 'forModel' does not exist on type 'never'.
                (part) => part.forModel === modelName,
            );

            // Find the first unlocked one, but if none are unlocked (which
            // hopefully means the entire avatar is unlocked), we still let
            // them preview it - just choose the first one in that case.
            const bestStyle =
                _.find(matchingStyles, (style) => style.isAvailable) ||
                matchingStyles[0];

            const selectedPart = bestStyle.name;
            this._changedPartType = "model";
            this.setState({
                selectedModel: modelName,
                selectedPart,
            });
        }
    }

    _setSelectedPartFromName(partName: string) {
        const style = _.findWhere(this._allAvatarStyles, {name: partName});
        if (style) {
            // @ts-expect-error - TS2339 - Property 'forModel' does not exist on type 'never'.
            this._setSelectedModel(style.forModel);
        }

        this._setSelectedPart(partName);
    }

    _setSelectedPart(partName: string) {
        const part = this._getAvatarPart(partName);
        const model = _.findWhere(this.state.avatarModels, {
            name: this.state.selectedModel,
        });

        if (!(part && part.isAvailable && model && model.isAvailable)) {
            return;
        }

        const selectedPart = partName;
        this._changedPartType = "style";
        this.setState({selectedPart});
    }

    _getActivePart(): string {
        return this.state.selectedPart;
    }

    _incrementModelBy(number: number) {
        // TODO(david): Take into account locked models -- skip past those.
        const selectedModel = this.state.selectedModel;
        const currentModelIndex = this._indexOfAvatarModel[selectedModel];
        const newModelIndex =
            (currentModelIndex + number + this.state.avatarModels.length) %
            this.state.avatarModels.length;
        const newModel = this.state.avatarModels[newModelIndex];
        this._modelIncrementedBy = number;
        this._setSelectedModel(newModel.name);
    }

    _goBackToAvatarPreview(selectedType: AvatarPartType) {
        this.setState({
            showingModelGrid: false,
            selectedType: selectedType,
        });
    }

    handlePartTypeClick: (partType: AvatarPartType) => void = (partType) => {
        this.setState({selectedType: partType});
    };

    handlePartClick: (partName: string) => void = (partName) => {
        this._setSelectedPart(partName);
    };

    handlePrevModelClick: () => void = () => {
        this._incrementModelBy(-1);
    };

    handleNextModelClick: () => void = () => {
        this._incrementModelBy(1);
    };

    handleShowModelGridClick: () => void = () => {
        this._prevSelectedType = this.state.selectedType;
        this.setState({
            showingModelGrid: true,
            selectedType: "model",
        });
    };

    handleShowAvatarPreviewClick: () => void = () => {
        this._goBackToAvatarPreview(
            this._prevSelectedType || this.state.selectedType,
        );
    };

    handleModelGridPartClick: (modelName: string) => void = (modelName) => {
        const {schedule} = this.props;
        this._setSelectedModel(modelName);

        // We wait 100ms for the select part transition to complete.
        schedule.timeout(() => this._goBackToAvatarPreview("style"), 100);
    };

    handleWindowKeyUp: (e: React.KeyboardEvent) => void = (e) => {
        const keyCode = e.which;

        if (keyCode === /* right arrow key */ 39) {
            this._incrementModelBy(1);
        } else if (keyCode === /* left arrow key */ 37) {
            this._incrementModelBy(-1);
        }
    };

    handleSaveClick: () => void = () => {
        const propsToSave: any = {};

        // Even though we don't actually save the image source, we need to
        // pass the imageSrc for avatars to saveAndUpdateProfile because it
        // does an eager update of the data. Since we know what it is here, we
        // can make the UI respond immediately.
        const selectedStyleName = this.state.selectedPart;
        if (selectedStyleName) {
            const selectedStyle = _.findWhere(this._allAvatarStyles, {
                name: selectedStyleName,
            });

            propsToSave.avatar = {
                name: selectedStyleName,
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                imageSrc: selectedStyle.imageSrc,
            };
        }

        saveAndUpdateProfile(this.props.gqlClient, propsToSave);

        this.props.onClose();
    };

    render(): React.ReactNode {
        // TODO(david): Make consistent the "Name" suffix of these variables
        const selectedPartName = this.state.selectedPart;
        const selectedPart = this._getAvatarPart(selectedPartName);

        const selectedModelName = this.state.selectedModel;
        const selectedModel = this.state.avatarModels.find(
            (model) => model.name === selectedModelName,
        );
        const modelStyles = this._allAvatarStyles.filter(
            // @ts-expect-error - TS2339 - Property 'forModel' does not exist on type 'never'.
            (stylePart) => stylePart.forModel === selectedModelName,
        );

        const allowSave =
            selectedPart &&
            selectedPart.isAvailable &&
            selectedModel &&
            selectedModel.isAvailable;

        // Compute the selected style to render the preview.
        const selectedStyleName = this._getActivePart();
        let selectedStyle: AvatarPart | undefined;
        if (selectedStyleName) {
            selectedStyle = modelStyles.find(
                (style: AvatarPart) => style.name === selectedStyleName,
            );
        }

        // If no style is selected or the selected style doesn't match a style
        // that is available for the selected model, take the first style
        // available for the selected model.
        // TODO(david): It should be the server's job to ensure that each
        //     UserData has avatar info, including style.
        if (!selectedStyle) {
            selectedStyle = modelStyles.find(
                (style: AvatarPart) => style.isAvailable,
            );
        }

        // TODO(david): Using the cached value of this._modelIncrementedBy
        //     seems hacky. Can we derive things directly from state?
        let modelChangeAnimation: AvatarsAnimation = "";
        const modelIncrementedBy = this._modelIncrementedBy;
        if (modelIncrementedBy && _.isNumber(modelIncrementedBy)) {
            modelChangeAnimation =
                modelIncrementedBy > 0 ? "slide-left" : "slide-right";
        }

        let previewChangeAnimation = modelChangeAnimation;
        if (this._changedPartType === "style") {
            previewChangeAnimation = "";
        }

        const frontVisible = !this.state.showingModelGrid;
        const cardFront = (
            <div style={{height: "100%"}}>
                <div className="avatar-preview-container">
                    {!!selectedStyle && !!selectedModel && (
                        <AvatarPreview
                            avatarModel={selectedModel}
                            avatarStyle={selectedStyle}
                            animation={previewChangeAnimation}
                        />
                    )}
                    <button
                        aria-label={t`Previous`}
                        onClick={this.handlePrevModelClick}
                        className="button-reset prev-model-button"
                        tabIndex={frontVisible ? 0 : -1}
                    >
                        <i className="icon-chevron-left" />
                    </button>
                    <button
                        aria-label={t`Next`}
                        onClick={this.handleNextModelClick}
                        className="button-reset next-model-button"
                        tabIndex={frontVisible ? 0 : -1}
                    >
                        <i className="icon-chevron-right" />
                    </button>
                </div>
                <AvatarPartsAccordion
                    modelStyles={modelStyles}
                    animation={modelChangeAnimation}
                    onPartTypeClick={this.handlePartTypeClick}
                    onPartClick={this.handlePartClick}
                    selectedModel={selectedModel}
                    selectedPart={selectedPartName}
                    visible={frontVisible}
                />
            </div>
        );

        const cardBack = (
            <AvatarDrawer
                className="avatar-models-grid"
                avatarModels={this.state.avatarModels}
                avatarPartType="model"
                onPartClick={this.handleModelGridPartClick}
                selectedPart={this.state.selectedModel}
                showPartName={true}
                visible={this.state.showingModelGrid}
            />
        );

        return (
            <div className="avatar-customizer-inner">
                <div className={css(styles.background)} />
                <FlippableCardView
                    showBack={this.state.showingModelGrid}
                    frontComponent={cardFront}
                    backComponent={cardBack}
                />
                <ViewSwitcherButton
                    selectedModel={selectedModel}
                    showingModelGrid={this.state.showingModelGrid}
                    onShowModelGridClick={this.handleShowModelGridClick}
                    onShowAvatarPreviewClick={this.handleShowAvatarPreviewClick}
                />
                <div className="save-and-cancel-buttons">
                    <Button
                        kind="secondary"
                        style={actionStyles.inverse}
                        onClick={this.props.onClose}
                    >
                        {t`Cancel`}
                    </Button>
                    <div className="save-button-container">
                        <Button
                            type="submit"
                            onClick={this.handleSaveClick}
                            disabled={!allowSave}
                            style={{minWidth: 200}}
                        >
                            {t`Save`}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

type ExportProps = WithoutActionScheduler<
    WithoutApollo<
        WithoutLocation<
            JSX.LibraryManagedAttributes<
                typeof AvatarCustomizer,
                React.ComponentProps<typeof AvatarCustomizer>
            >
        >
    >
>;

export default compose(
    withLocation,
    withApollo,
    withActionScheduler,
)(AvatarCustomizer) as React.ComponentType<ExportProps>;
